import { Mail, MessageCircle, MapPin, ShieldCheck } from "lucide-react";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/LanguageProvider";
import { useRegisterAlternates } from "../components/layout/Layout";
import { pagePath } from "../config/pages";
import { SITE, WHATSAPP_LINK } from "../config/site";
import { PageHero } from "../components/Sections";
import { Reveal } from "../components/motion/Reveal";
import ConsultationForm from "../components/ConsultationForm";
import { webPageSchema, breadcrumbSchema, organizationSchema } from "../lib/schema";

const META = {
  title: {
    en: "Contact & Free Treatment Plan | Dentel Vietnam",
    fr: "Contact & plan de traitement gratuit | Dentel Vietnam",
    de: "Kontakt & kostenloser Behandlungsplan | Dentel Vietnam",
  },
  desc: {
    en: "Request your free, no-obligation treatment plan. Send your details and dental photos or X-rays for an initial case review.",
    fr: "Demandez votre plan de traitement gratuit et sans engagement. Envoyez vos informations et photos ou radiographies pour une première évaluation.",
    de: "Fordern Sie Ihren kostenlosen, unverbindlichen Behandlungsplan an. Senden Sie Ihre Angaben und Fotos oder Röntgenbilder für eine erste Prüfung.",
  },
  h1: { en: "Get Your Free Treatment Plan", fr: "Obtenez votre plan de traitement gratuit", de: "Ihr kostenloser Behandlungsplan" },
};

export default function Contact() {
  const { t, lang, p } = useI18n();
  const alternates = { en: pagePath("contact", "en"), fr: pagePath("contact", "fr"), de: pagePath("contact", "de") };
  useRegisterAlternates(alternates);
  const path = pagePath("contact", lang);
  const title = META.h1[lang] || META.h1.en;

  return (
    <>
      <Seo
        title={META.title[lang] || META.title.en}
        description={META.desc[lang] || META.desc.en}
        path={path}
        alternates={alternates}
        lang={lang}
        jsonLd={[
          organizationSchema(),
          webPageSchema({ name: title, description: META.desc[lang] || META.desc.en, path }),
          breadcrumbSchema([{ name: t.common.home, path: p("home") }, { name: title, path }]),
        ]}
      />
      <PageHero eyebrow={t.cta.freePlan} title={title} intro={t.form.sub} crumbs={[{ name: t.nav.contact, to: path }]} />

      <section className="container-dv py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="text-2xl text-navy">{SITE.brand}</h2>
              <p className="mt-3 text-navy/70 leading-relaxed">{t.footer.about}</p>

              <div className="mt-8 space-y-4">
                <a href={`mailto:${SITE.email}`} data-testid="contact-email" className="flex items-center gap-4 rounded-2xl border border-navy/8 bg-white p-5 shadow-soft hover:border-dental/30 transition-colors">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-dental/8 text-dental"><Mail className="h-5 w-5" /></span>
                  <span><span className="block text-xs uppercase tracking-wide text-navy/45">{t.form.email}</span><span className="font-semibold text-navy">{SITE.email}</span></span>
                </a>
                <a href={WHATSAPP_LINK()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 rounded-2xl border border-navy/8 bg-white p-5 shadow-soft hover:border-turquoise/40 transition-colors">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-turquoise/10 text-turquoise"><MessageCircle className="h-5 w-5" /></span>
                  <span><span className="block text-xs uppercase tracking-wide text-navy/45">WhatsApp</span><span className="font-semibold text-navy">{SITE.whatsappDisplay}</span></span>
                </a>
                <div className="flex items-center gap-4 rounded-2xl border border-navy/8 bg-white p-5 shadow-soft">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-navy/5 text-navy"><MapPin className="h-5 w-5" /></span>
                  <span><span className="block text-xs uppercase tracking-wide text-navy/45">{t.footer.contact}</span><span className="font-semibold text-navy">{SITE.address.city}, {SITE.address.country}</span></span>
                </div>
              </div>

              <div className="mt-8 flex gap-3 rounded-2xl border border-turquoise/20 bg-turquoise/5 p-5 text-sm leading-relaxed text-navy/70">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-turquoise" />
                <span>{t.footer.disclaimerShort}</span>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.05}>
              <ConsultationForm />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
